/**
 * Formats a number string with thousand separators (commas)
 * @param value - The numeric string to format
 * @returns Formatted string with commas
 */
export function formatNumberWithCommas(value: string | number): string {
  if (value === "" || value === null || value === undefined) return "";

  const stringValue = String(value);

  // Handle negative numbers
  const isNegative = stringValue.startsWith("-");
  const absValue = isNegative ? stringValue.slice(1) : stringValue;

  // Split into integer and decimal parts
  const parts = absValue.split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Add commas to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Reconstruct the number
  let result = formattedInteger;
  if (decimalPart !== undefined) {
    result += `.${decimalPart}`;
  }

  return isNegative ? `-${result}` : result;
}

/**
 * Removes thousand separators (commas) from a formatted number string
 * @param value - The formatted string to parse
 * @returns Clean numeric string
 */
export function parseFormattedNumber(value: string): string {
  if (value === "" || value === null || value === undefined) return "";
  return String(value).replace(/,/g, "");
}

/**
 * Handles input change for formatted number fields
 * Maintains cursor position while formatting
 * @param event - The input change event
 * @param setValue - React Hook Form setValue function
 * @param fieldName - The field name to update
 */
export function handleFormattedNumberChange(
  event: React.ChangeEvent<HTMLInputElement>,
  setValue: (name: string, value: string) => void,
  fieldName: string
): void {
  const input = event.target;
  const cursorPosition = input.selectionStart || 0;
  const rawValue = input.value;

  // Remove all commas to get the raw number
  const cleanValue = parseFormattedNumber(rawValue);

  // Check if it's a valid number input
  if (cleanValue !== "" && Number.isNaN(Number(cleanValue))) {
    return; // Don't update if not a valid number
  }

  // Count commas before cursor
  const commasBeforeCursor = (rawValue.slice(0, cursorPosition).match(/,/g) || []).length;

  // Format the number
  const formattedValue = formatNumberWithCommas(cleanValue);

  // Update the value
  setValue(fieldName, formattedValue);

  // Calculate new cursor position
  const commasInFormatted = (formattedValue.slice(0, cursorPosition).match(/,/g) || []).length;
  const newPosition = cursorPosition + (commasInFormatted - commasBeforeCursor);

  // Restore cursor position after React updates
  setTimeout(() => {
    input.setSelectionRange(newPosition, newPosition);
  }, 0);
}
